/**
 * Meta Embedded Signup, browser half.
 *
 * This is the only module that knows how a merchant's WhatsApp Business Account is obtained.
 * It runs `FB.login` with `response_type: 'code'` and reads the WABA id and phone number id off
 * the `WA_EMBEDDED_SIGNUP` postMessage that Meta sends with `sessionInfoVersion: '3'`.
 *
 * The authorization code goes straight to `POST /console/whatsapp/exchange`. The browser never
 * sees a Meta access token: the code→token exchange needs the app secret and happens server-side.
 * See ADR 0004 and packages/backend-infra/docs/meta-embedded-signup-runbook.md.
 */
const facebookMessageOrigins = new Set(['https://www.facebook.com', 'https://web.facebook.com']);
const sessionInfoWaitTimeoutMs = 10_000;
const sdkScriptId = 'facebook-jssdk';
const metaLocale = 'pt_BR';
const sdkSrc = `https://connect.facebook.net/${metaLocale}/sdk.js`;
const embeddedSignupBusinessId = '1338034461642831';

export interface MetaSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string | null;
}

export type MetaSignupOutcome =
  | { kind: 'ok'; result: MetaSignupResult }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

export interface MetaSignupClient {
  isConfigured(): boolean;
  launch(): Promise<MetaSignupOutcome>;
}

interface FacebookLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

interface FacebookSdk {
  init(options: Record<string, unknown>): void;
  login(callback: (response: FacebookLoginResponse) => void, options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

export interface CreateMetaSignupClientInput {
  appId: string | null;
  configId: string | null;
  graphApiVersion?: string;
}

function readSdkScript(): HTMLScriptElement | null {
  const existing = document.getElementById(sdkScriptId);

  if (!(existing instanceof HTMLScriptElement)) {
    return null;
  }

  return existing;
}

function createEmbeddedSignupExtras(): Record<string, unknown> {
  return {
    setup: {
      business: { id: embeddedSignupBusinessId },
    },
    featureType: 'whatsapp_business_app_onboarding',
    sessionInfoVersion: '3',
  };
}

export function createMetaSignupClient(input: CreateMetaSignupClientInput): MetaSignupClient {
  const version = input.graphApiVersion ?? 'v21.0';
  let sdkPromise: Promise<FacebookSdk> | null = null;

  function loadSdk(appId: string): Promise<FacebookSdk> {
    if (sdkPromise) {
      return sdkPromise;
    }

    sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
      if (window.FB) {
        const existing = readSdkScript();

        console.warn('[Meta signup] Facebook SDK was already loaded; cannot safely reload locale.', {
          targetLocale: metaLocale,
          expectedSdkSrc: sdkSrc,
          currentSdkSrc: existing?.src ?? null,
        });

        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
        resolve(window.FB);

        return;
      }

      window.fbAsyncInit = () => {
        if (!window.FB) {
          reject(new Error('Não foi possível abrir o login da Meta. Tente novamente.'));

          return;
        }

        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
        resolve(window.FB);
      };

      const existing = readSdkScript();

      if (existing && existing.src !== sdkSrc) {
        console.info('[Meta signup] Removing Facebook SDK script loaded with a different locale.', {
          targetLocale: metaLocale,
          expectedSdkSrc: sdkSrc,
          currentSdkSrc: existing.src,
        });
        existing.remove();
      }

      if (readSdkScript()) {
        return;
      }

      const script = document.createElement('script');
      script.id = sdkScriptId;
      script.src = sdkSrc;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () =>
        reject(new Error('Não foi possível abrir o login da Meta. Verifique sua conexão e tente novamente.'));
      document.body.appendChild(script);
    });

    return sdkPromise;
  }

  return {
    isConfigured() {
      return Boolean(input.appId && input.configId);
    },

    async launch(): Promise<MetaSignupOutcome> {
      if (!input.appId || !input.configId) {
        return { kind: 'error', message: 'A conexão com a Meta está indisponível no momento.' };
      }

      const sdk = await loadSdk(input.appId);
      const sessionInfo = listenForSessionInfo();

      const loginResponse = await new Promise<FacebookLoginResponse>((resolve) => {
        sdk.login((response) => resolve(response), {
          config_id: input.configId,
          response_type: 'code',
          override_default_response_type: true,
          // The SDK script URL is Meta's documented localization mechanism; this is best-effort.
          locale: metaLocale,
          extras: createEmbeddedSignupExtras(),
        });
      });

      console.info('[Meta signup] FB.login returned', {
        status: loginResponse.status,
        hasAuthCode: Boolean(loginResponse.authResponse?.code),
      });

      const info = await sessionInfo.waitFor(
        (candidate) => candidate.event === 'CANCEL' || Boolean(candidate.wabaId),
        sessionInfoWaitTimeoutMs,
      );
      sessionInfo.stop();

      if (info?.event === 'CANCEL') {
        return { kind: 'cancelled' };
      }

      const code = loginResponse.authResponse?.code;

      if (!code) {
        return { kind: 'cancelled' };
      }

      if (!info?.wabaId) {
        return {
          kind: 'error',
          message: 'A Meta autorizou o acesso, mas ainda não informou a conta do WhatsApp conectada. Aguarde alguns segundos e tente novamente.',
        };
      }

      return {
        kind: 'ok',
        result: { code, wabaId: info.wabaId, phoneNumberId: info.phoneNumberId },
      };
    },
  };
}

interface SessionInfo {
  event: string;
  wabaId: string | null;
  phoneNumberId: string | null;
}

function listenForSessionInfo() {
  let latest: SessionInfo | null = null;
  let pending:
    | {
        predicate: (info: SessionInfo) => boolean;
        resolve: (info: SessionInfo | null) => void;
        timeout: ReturnType<typeof setTimeout>;
      }
    | null = null;

  function resolvePending(info: SessionInfo): void {
    if (!pending || !pending.predicate(info)) {
      return;
    }

    clearTimeout(pending.timeout);
    pending.resolve(info);
    pending = null;
  }

  function handleMessage(event: MessageEvent) {
    // Origin-checked: without this, any embedded frame could claim a WABA connected.
    if (!facebookMessageOrigins.has(event.origin)) {
      return;
    }

    const parsed = parseSignupMessage(event.data);

    if (!parsed) {
      return;
    }

    console.info('[Meta signup] WA_EMBEDDED_SIGNUP message parsed', {
      event: parsed.event,
      hasWabaId: Boolean(parsed.wabaId),
      hasPhoneNumberId: Boolean(parsed.phoneNumberId),
    });

    latest = parsed;
    resolvePending(parsed);
  }

  window.addEventListener('message', handleMessage);

  return {
    waitFor(predicate: (info: SessionInfo) => boolean, timeoutMs: number) {
      if (latest && predicate(latest)) {
        return Promise.resolve(latest);
      }

      return new Promise<SessionInfo | null>((resolve) => {
        pending = {
          predicate,
          resolve,
          timeout: setTimeout(() => {
            resolve(latest);
            pending = null;
          }, timeoutMs),
        };
      });
    },
    stop: () => {
      if (pending) {
        clearTimeout(pending.timeout);
        pending.resolve(latest);
        pending = null;
      }

      window.removeEventListener('message', handleMessage);
    },
  };
}

function parseSignupMessage(data: unknown): SessionInfo | null {
  const payload = readJson(data);

  if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
    return null;
  }

  const body = readRecord(payload.data);

  return {
    event: readText(payload.event),
    wabaId: readOptionalText(body?.waba_id),
    phoneNumberId: readOptionalText(body?.phone_number_id),
  };
}

function readJson(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    try {
      return readRecord(JSON.parse(data));
    } catch {
      return null;
    }
  }

  return readRecord(data);
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readText(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value;
}

function readOptionalText(value: unknown): string | null {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  return value;
}
